import React from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { motion } from "framer-motion";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";

const DOT_GRID_LIGHT = "radial-gradient(circle, rgba(0,53,122,0.08) 1.2px, transparent 1.2px)";
const DOT_GRID_DARK  = "radial-gradient(circle, rgba(255,255,255,0.04) 1.2px, transparent 1.2px)";

export default function PageShell({ children }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const bg = isDark ? "#080d1a" : "#f4f7ff";

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: bg,
      display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
    }}>
      {/* Dot-grid texture */}
      <Box sx={{
        position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
        backgroundImage: isDark ? DOT_GRID_DARK : DOT_GRID_LIGHT,
        backgroundSize: "28px 28px", opacity: 0.85,
      }} />

      {/* Ambient colour blobs */}
      <Box sx={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-5%",  left: "-4%",  size: 480, color: isDark ? "rgba(0,53,122,0.18)" : "rgba(0,53,122,0.06)" },
          { top: "60%",  right: "-5%", size: 360, color: isDark ? "rgba(201,169,110,0.08)" : "rgba(201,169,110,0.1)" },
        ].map((b, i) => (
          <motion.div key={i} style={{
            position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%",
            background: b.color, filter: "blur(90px)",
          }}
            animate={{ scale: [1, 1.08, 1], opacity: [0.75, 1, 0.75] }}
            transition={{ duration: 10 + i * 3, repeat: 2, delay: i * 2, ease: "easeInOut" }}
          />
        ))}
      </Box>

      {/* ── Navbar ── */}
      <motion.div initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        style={{ flexShrink: 0, zIndex: 20, position: "sticky", top: 0 }}>
        <Box sx={{
          bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(0,31,77,0.96)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(201,169,110,0.2)",
          boxShadow: "0 4px 28px rgba(0,31,77,0.2)",
        }}>
          {/* Gold accent line — top */}
          <Box sx={{
            height: 2,
            background: "linear-gradient(90deg, transparent, rgba(201,169,110,0.8) 30%, rgba(223,192,138,0.9) 50%, rgba(201,169,110,0.8) 70%, transparent)",
          }} />
          {/* 2026-10-04 fix — real overflow bug, same root cause fixed across
              every other navbar in this app (RequisitionConversion.js's
              TopBar, Admin/Audit/Rcm/User Dashboard): fixed height + no
              flexWrap means this can't fit on a narrower viewport. This is
              the SHARED shell most pages render through, so the fix here
              has the widest reach of any single navbar fix in the app. */}
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 68,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                onClick={() => navigate("/")}
                sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  color: "rgba(255,255,255,0.55)", fontSize: 14.5,
                  fontFamily: "'Inter', sans-serif",
                  cursor: "pointer", borderRadius: "8px", px: 1.5, py: 0.7,
                  transition: "all 0.2s",
                  "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 15 }} />
                <span>Home</span>
              </Box>
              <Box sx={{ width: "1px", height: 22, bgcolor: "rgba(255,255,255,0.12)" }} />
              <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 38, width: "auto" }} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ThemeToggle />
              <NavUserMenu />
            </Box>
          </Box>
        </Box>
      </motion.div>

      {/* Page content */}
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        style={{ flex: 1, position: "relative", zIndex: 10 }}>
        {children}
      </motion.div>

      {/* Footer */}
      <Box sx={{
        bgcolor: isDark ? "rgba(2,6,15,0.92)" : "#001f4d",
        backdropFilter: "blur(10px)",
        py: 2.5, textAlign: "center",
        borderTop: "1px solid rgba(201,169,110,0.12)",
        position: "relative", zIndex: 10,
      }}>
        <Box component="span" sx={{
          fontSize: 12.5, color: "rgba(255,255,255,0.2)", letterSpacing: "0.05em",
          fontFamily: "'Inter', sans-serif",
        }}>
          © {new Date().getFullYear()} LABARIUM DIAGNOSTIC SYSTEMS, INC. — AI ANALYSIS DOES NOT CONSTITUTE MEDICAL ADVICE.
        </Box>
      </Box>
    </Box>
  );
}
